<template>
  <div class="edit-mode-selector d-inline-block">
    <b-dropdown
      :variant="dropdownVariant"
      :disabled="isClassifying"
      menu-class="edit-mode-dropdown-menu"
      right
      no-flip
      toggle-class="edit-mode-toggle-btn"
      @shown="onDropdownShown"
      @hidden="onDropdownHidden"
    >
      <template #button-content>
        <b-spinner
          v-if="isClassifying"
          small
          class="mr-2"
        />
        <b-icon
          v-else
          :icon="currentModeIcon"
          class="mr-2"
        />
        {{ currentModeLabel }}
        <b-icon
          icon="chevron-down"
          class="ml-2 dropdown-caret"
          font-scale="0.8"
        />
      </template>

      <b-dropdown-item
        v-for="mode in editModes"
        :key="mode.value"
        :active="currentMode === mode.value"
        @click="changeMode(mode.value)"
      >
        <b-icon
          :icon="mode.icon"
          class="mr-2"
        />
        {{ mode.label }}
        <small
          v-if="mode.description"
          class="d-block edit-mode-description"
          :class="{ 'description-active': currentMode === mode.value }"
        >
          {{ mode.description }}
        </small>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import { editModeManager } from '@frontend/services/EditModeManager';
import { editModeService } from '@frontend/services/EditModeService';
import { EDIT_MODE, EDIT_MODE_LABELS } from '@frontend/constants/edit-mode';

export default {
  name: 'EditModeSelector',

  data() {
    return {
      currentMode: editModeService.getEditMode(),
      isClassifying: false,
      editModes: [
        {
          value: EDIT_MODE.FULL_EDIT,
          label: EDIT_MODE_LABELS[EDIT_MODE.FULL_EDIT],
          icon: 'unlock',
          description: 'Edit everything',
        },
        {
          value: EDIT_MODE.TEXT_PRODUCT_IMAGE,
          label: EDIT_MODE_LABELS[EDIT_MODE.TEXT_PRODUCT_IMAGE],
          icon: 'pencil',
          description: 'Text, products, and backgrounds',
        },
        {
          value: EDIT_MODE.TEXT_PRODUCT,
          label: EDIT_MODE_LABELS[EDIT_MODE.TEXT_PRODUCT],
          icon: 'fonts',
          description: 'Text and products only',
        },
        {
          value: EDIT_MODE.PRODUCT_ONLY,
          label: EDIT_MODE_LABELS[EDIT_MODE.PRODUCT_ONLY],
          icon: 'lock',
          description: 'Only swap product images',
        },
      ],
    };
  },

  computed: {
    currentModeLabel() {
      if (this.isClassifying) {
        return 'Classifying...';
      }
      return EDIT_MODE_LABELS[this.currentMode] || 'Full Edit';
    },

    currentModeIcon() {
      const mode = this.editModes.find(m => m.value === this.currentMode);
      return mode?.icon || 'unlock';
    },

    currentModeDescription() {
      const mode = this.editModes.find(m => m.value === this.currentMode);
      return mode?.description || '';
    },

    dropdownVariant() {
      // Use custom class for all modes - we'll style them with pastel colors
      if (this.isClassifying) return 'edit-mode-classifying';
      if (this.currentMode === EDIT_MODE.PRODUCT_ONLY) return 'edit-mode-product';
      if (this.currentMode === EDIT_MODE.TEXT_PRODUCT) return 'edit-mode-text-product';
      if (this.currentMode === EDIT_MODE.TEXT_PRODUCT_IMAGE) return 'edit-mode-text';
      return 'edit-mode-full';
    },
  },

  mounted() {
    // Sync mode from saved state immediately
    const savedMode = window.layoutEditMode || editModeService.getEditMode();
    if (savedMode && savedMode !== this.currentMode) {
      this.currentMode = savedMode;
      editModeService.setEditMode(savedMode);
    }

    // Listen for mode changes from EditModeManager
    editModeManager.onModeChange((mode, classifying) => {
      this.currentMode = mode;
      this.isClassifying = classifying;
    });

    // Initialize restrictions when previews are ready (based on fieldType)
    this._initRestrictionsWhenReady();
  },

  methods: {
    _initRestrictionsWhenReady(attempts = 0) {
      const maxAttempts = 30; // Try for up to 30 seconds
      const previews = window.previews || [];

      if (previews.length > 0) {
        // Sync current mode from service (in case it was loaded from saved state)
        const savedMode = editModeService.getEditMode();
        if (savedMode !== this.currentMode) {
          this.currentMode = savedMode;
        }

        // If we're in a restricted mode, apply restrictions now based on fieldType
        if (this.currentMode !== 'full_edit') {
          editModeManager._applyRestrictions(previews, true);
        }
      } else if (attempts < maxAttempts) {
        setTimeout(() => this._initRestrictionsWhenReady(attempts + 1), 1000);
      }
    },

    onDropdownShown() {
      const stickyPlacement = document.querySelector('.sticky-placement');
      if (stickyPlacement) {
        stickyPlacement.classList.add('edit-mode-dropdown-open');
      }
    },

    onDropdownHidden() {
      const stickyPlacement = document.querySelector('.sticky-placement');
      if (stickyPlacement) {
        stickyPlacement.classList.remove('edit-mode-dropdown-open');
      }
    },

    async changeMode(newMode) {
      if (newMode === this.currentMode) return;

      this.isClassifying = true;

      try {
        const templateId = window.template_data?.id;
        const layoutId = window.layoutId;
        const previews = window.previews || [];

        await editModeManager.changeEditMode(newMode, {
          previews,
          templateId,
          layoutId,
        });

        this.currentMode = newMode;
      } catch (error) {
        console.error('Failed to change edit mode:', error);
        this.$bvToast.toast('Failed to change edit mode', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.isClassifying = false;
      }
    },
  },
};
</script>

<style scoped>
.edit-mode-selector {
  /* Spacing handled by parent element's mr-2 class */
}
</style>

<!-- eslint-disable vue/enforce-style-attribute -->
<style>
/* Unscoped to target Bootstrap dropdown menu and button */
.edit-mode-dropdown-menu {
  z-index: 1035 !important;
  min-width: 220px;
}

/* Match button style with other header buttons in layout view */
.edit-mode-toggle-btn {
  border-radius: 8px !important;
  height: 38px !important;
  padding: 8px 12px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
  display: inline-flex !important;
  align-items: center !important;
}

/* Dropdown caret styling */
.edit-mode-toggle-btn .dropdown-caret {
  opacity: 0.8;
}

/* Hide Bootstrap's default dropdown caret - we use our own */
.edit-mode-toggle-btn.dropdown-toggle::after {
  display: none !important;
}

/* Full Edit mode - neutral gray (default state) */
.edit-mode-toggle-btn.btn-edit-mode-full {
  background-color: #F9FAFB !important;
  border: 1px solid #D2D4D6 !important;
  color: #344054 !important;
}

.edit-mode-toggle-btn.btn-edit-mode-full:hover {
  background-color: #F3F4F6 !important;
  border: 1px solid #D2D4D6 !important;
  box-shadow: 0px 1px 2px 0px #1018280D !important;
}

/* Text + Product + Image mode - soft teal/mint pastel */
.edit-mode-toggle-btn.btn-edit-mode-text {
  background-color: #E0F2F1 !important;
  border: 1px solid #B2DFDB !important;
  color: #00796B !important;
}

.edit-mode-toggle-btn.btn-edit-mode-text:hover {
  background-color: #B2DFDB !important;
  border: 1px solid #80CBC4 !important;
}

/* Text + Product mode - soft sky blue pastel */
.edit-mode-toggle-btn.btn-edit-mode-text-product {
  background-color: #E3F2FD !important;
  border: 1px solid #BBDEFB !important;
  color: #1565C0 !important;
}

.edit-mode-toggle-btn.btn-edit-mode-text-product:hover {
  background-color: #BBDEFB !important;
  border: 1px solid #90CAF9 !important;
}

/* Product-Only mode - soft lavender/purple pastel */
.edit-mode-toggle-btn.btn-edit-mode-product {
  background-color: #EDE7F6 !important;
  border: 1px solid #D1C4E9 !important;
  color: #5E35B1 !important;
}

.edit-mode-toggle-btn.btn-edit-mode-product:hover {
  background-color: #D1C4E9 !important;
  border: 1px solid #B39DDB !important;
}

/* Classifying state - warm amber pastel (slightly darker for visibility) */
.edit-mode-toggle-btn.btn-edit-mode-classifying {
  background-color: #FFECB3 !important;
  border: 1px solid #FFD54F !important;
  color: #E65100 !important;
}

.edit-mode-toggle-btn.btn-edit-mode-classifying:hover {
  background-color: #FFE082 !important;
  border: 1px solid #FFCA28 !important;
}

/* Description text in dropdown items */
.edit-mode-description {
  color: #6c757d;
  font-size: 11px;
  margin-top: 2px;
}

/* Description text when item is active (selected) */
.description-active {
  color: rgba(255, 255, 255, 0.85) !important;
}

/* Dropdown item styling */
.edit-mode-dropdown-menu .dropdown-item {
  padding: 8px 16px;
}

.edit-mode-dropdown-menu .dropdown-item.active {
  background-color: #20a8d8;
}
</style>
